/**
 * Curated dataset of major emergency services across Mali for the Carte tab.
 *
 * Each entry has a `category` matching the layer-toggle pills, exact GPS,
 * a citizen-friendly French name, and an optional `phone` for one-tap dial.
 *
 * Coordinates are approximate (sourced from open mapping data); good enough to
 * route a citizen to the right block — final navigation handed off to
 * Google/Apple Maps via `https://www.google.com/maps/dir/?api=1&destination=…`.
 */

export const SERVICE_CATEGORIES = [
  { key: "hospital", label: "Hôpitaux",       short: "Hôpital",   color: "#10B981", testid: "svc-hospital" },
  { key: "police",   label: "Police",         short: "Police",    color: "#1E3A8A", testid: "svc-police" },
  { key: "fire",     label: "Pompiers",       short: "Pompiers",  color: "#EA580C", testid: "svc-fire" },
  { key: "fama",     label: "FAMA",           short: "FAMA",      color: "#7C2D12", testid: "svc-fama" },
  { key: "embassy",  label: "Ambassades",     short: "Ambassade", color: "#9333EA", testid: "svc-embassy" },
];

export const SERVICE_META = Object.fromEntries(
  SERVICE_CATEGORIES.map((s) => [s.key, s])
);

export const EMERGENCY_SERVICES = [
  // ── Hospitals (Bamako + regions) ──────────────────────────────────────
  // Coordinates verified against OpenStreetMap May 2026. Point G, Gabriel
  // Touré and Hôpital du Mali are the highest-traffic destinations so
  // their locations are the most important to keep precise.
  //
  // Sep 2026 — "Hôpital du Mali", "Hôpital de Sikasso" and "Hôpital de
  // Kati" coordinates below were corrected against Google Places
  // (authoritative provider as of Iter79). The OLD values here were off
  // by up to ~12 km (this list is an OFFLINE FALLBACK only — the live
  // map always prefers `/api/facilities`, see useFacilities.js — but a
  // wrong fallback coordinate is still dangerous if a citizen is ever
  // genuinely offline, so it must stay accurate too).
  { id: "hosp-hgt",    category: "hospital", name: "Hôpital du Mali",                lat: 12.6340848, lng: -7.9115986, phone: "+22320229212", city: "Bamako" },
  { id: "hosp-pg",     category: "hospital", name: "Hôpital du Point G",             lat: 12.6547, lng: -8.0069, phone: "+22320222712", city: "Bamako" },
  { id: "hosp-gt",     category: "hospital", name: "Hôpital Gabriel Touré",          lat: 12.6478, lng: -7.9895, phone: "+22320229611", city: "Bamako" },
  { id: "hosp-luxe",   category: "hospital", name: "Hôpital Mère-Enfant Luxembourg", lat: 12.6206, lng: -8.0248, phone: "+22320293030", city: "Bamako" },
  { id: "hosp-mint",   category: "hospital", name: "Hôpital Mint (CHU-OS)",          lat: 12.6501, lng: -7.9877, phone: "+22320217575", city: "Bamako" },
  { id: "hosp-derma",  category: "hospital", name: "Centre Nat. d'Appui à la lutte contre la Maladie (CNAM)", lat: 12.6462, lng: -7.9752, phone: "", city: "Bamako" },
  { id: "hosp-yirim",  category: "hospital", name: "CSREF Commune VI (Yirimadio)",   lat: 12.5847, lng: -7.9110, phone: "+22320285050", city: "Bamako" },
  { id: "hosp-kati",   category: "hospital", name: "Hôpital de Kati",                lat: 12.7267, lng: -8.0607, phone: "",              city: "Kati" },
  { id: "hosp-sego",   category: "hospital", name: "Hôpital Nianankoro Fomba",       lat: 13.4341, lng: -6.2551, phone: "+22321320143", city: "Ségou" },
  { id: "hosp-mopti",  category: "hospital", name: "Hôpital Sominé Dolo",            lat: 14.5052, lng: -4.1946, phone: "+22321430010", city: "Mopti" },
  { id: "hosp-sika",   category: "hospital", name: "Hôpital de Sikasso",             lat: 11.3062, lng: -5.6864, phone: "+22321620172", city: "Sikasso" },
  { id: "hosp-kayes",  category: "hospital", name: "Hôpital Fousseyni Daou",         lat: 14.4460, lng: -11.4460, phone: "+22321521134", city: "Kayes" },
  { id: "hosp-gao",    category: "hospital", name: "Hôpital de Gao",                 lat: 16.2691, lng: -0.0433, phone: "",              city: "Gao" },
  { id: "hosp-tomb",   category: "hospital", name: "Hôpital de Tombouctou",          lat: 16.7749, lng: -3.0064, phone: "",              city: "Tombouctou" },

  // ── SAMU dispatch centers (medical emergency lines) ────────────────────
  { id: "samu-bko",    category: "hospital", name: "SAMU Bamako (Régulation)",       lat: 12.6478, lng: -7.9895, phone: "15",            city: "Bamako" },

  // ── Police / Gendarmerie ───────────────────────────────────────────────
  { id: "pol-cmt",     category: "police", name: "Direction Générale de la Police",     lat: 12.6371, lng: -7.9902, phone: "17",            city: "Bamako" },
  { id: "pol-i1",      category: "police", name: "Commissariat du 1er Arr. (Centre)",    lat: 12.6497, lng: -8.0004, phone: "17",            city: "Bamako" },
  { id: "pol-i3",      category: "police", name: "Commissariat du 3ème Arr.",            lat: 12.6314, lng: -7.9812, phone: "17",            city: "Bamako" },
  { id: "pol-aci",     category: "police", name: "Commissariat ACI 2000",                lat: 12.6208, lng: -8.0298, phone: "17",            city: "Bamako" },
  { id: "pol-cm5",     category: "police", name: "Commissariat Commune V (Badalabougou)", lat: 12.6216, lng: -7.9905, phone: "17",           city: "Bamako" },
  { id: "pol-gend",    category: "police", name: "Gendarmerie Nationale · Faladié",      lat: 12.6098, lng: -7.9459, phone: "80001100",      city: "Bamako" },
  { id: "pol-kati",    category: "police", name: "Commissariat de Kati",                 lat: 12.7404, lng: -8.0721, phone: "17",            city: "Kati" },
  { id: "pol-sego",    category: "police", name: "Commissariat de Ségou",                lat: 13.4425, lng: -6.2617, phone: "17",            city: "Ségou" },
  { id: "pol-mopti",   category: "police", name: "Commissariat de Mopti",                lat: 14.5010, lng: -4.1932, phone: "17",            city: "Mopti" },
  { id: "pol-sika",    category: "police", name: "Commissariat de Sikasso",              lat: 11.3184, lng: -5.6673, phone: "17",            city: "Sikasso" },
  { id: "pol-kayes",   category: "police", name: "Commissariat de Kayes",                lat: 14.4478, lng: -11.4440, phone: "17",           city: "Kayes" },

  // ── Fire / Protection Civile ──────────────────────────────────────────
  { id: "fire-bama1",  category: "fire", name: "Caserne Centrale · Bamako-Coura",     lat: 12.6404, lng: -7.9958, phone: "18",            city: "Bamako" },
  { id: "fire-aci",    category: "fire", name: "Caserne ACI 2000",                     lat: 12.6212, lng: -8.0272, phone: "18",            city: "Bamako" },
  { id: "fire-bad",    category: "fire", name: "Caserne Badalabougou",                 lat: 12.6270, lng: -7.9878, phone: "18",            city: "Bamako" },
  { id: "fire-kati",   category: "fire", name: "Caserne de Kati",                      lat: 12.7423, lng: -8.0691, phone: "18",            city: "Kati" },
  { id: "fire-sego",   category: "fire", name: "Caserne de Ségou",                     lat: 13.4408, lng: -6.2604, phone: "18",            city: "Ségou" },
  { id: "fire-mopti",  category: "fire", name: "Caserne de Mopti",                     lat: 14.5028, lng: -4.1928, phone: "18",            city: "Mopti" },

  // ── FAMA (Forces Armées Maliennes) bases / camps ──────────────────────
  { id: "fama-kati",   category: "fama", name: "Camp Soundiata · Kati",         lat: 12.7461, lng: -8.0668, phone: "144",           city: "Kati" },
  { id: "fama-djk",    category: "fama", name: "Quartier Général Bamako",       lat: 12.6378, lng: -8.0026, phone: "144",           city: "Bamako" },
  { id: "fama-sego",   category: "fama", name: "Garnison de Ségou",             lat: 13.4361, lng: -6.2715, phone: "144",           city: "Ségou" },
  { id: "fama-gao",    category: "fama", name: "Camp Firhoun · Gao",            lat: 16.2700, lng: -0.0500, phone: "144",           city: "Gao" },
  { id: "fama-mopti",  category: "fama", name: "Garnison de Mopti",             lat: 14.4980, lng: -4.2000, phone: "144",           city: "Mopti" },

  // ── Embassies (sample — major missions in Bamako) ─────────────────────
  { id: "emb-fr",      category: "embassy", name: "Ambassade de France",         lat: 12.6353, lng: -8.0035, phone: "+22344976950", city: "Bamako" },
  { id: "emb-us",      category: "embassy", name: "Ambassade des États-Unis",    lat: 12.6232, lng: -7.9420, phone: "+22320702300", city: "Bamako" },
  { id: "emb-de",      category: "embassy", name: "Ambassade d'Allemagne",       lat: 12.6303, lng: -7.9990, phone: "+22344987340", city: "Bamako" },
  { id: "emb-ru",      category: "embassy", name: "Ambassade de Russie",         lat: 12.6360, lng: -8.0070, phone: "+22320225135", city: "Bamako" },
  { id: "emb-ch",      category: "embassy", name: "Ambassade de Chine",          lat: 12.6178, lng: -8.0240, phone: "+22320722273", city: "Bamako" },
  { id: "emb-ml",      category: "embassy", name: "Ambassade du Maroc",          lat: 12.6321, lng: -8.0007, phone: "+22320213120", city: "Bamako" },
];

/**
 * Build a GeoJSON FeatureCollection of services filtered by a set of active
 * category keys. Used by MapLibre `addSource`.
 *
 * Iter78 — accepts an optional `facilities` array (from `useFacilities`);
 * when omitted, falls back to the legacy hardcoded `EMERGENCY_SERVICES`
 * list. Each item must expose `{id, type|category, name, lat, lng, phone?, city?}`.
 */
export const buildServicesGeoJSON = (activeCategories, facilities = null) => {
  const source = Array.isArray(facilities) && facilities.length
    ? facilities
    : EMERGENCY_SERVICES;
  return {
    type: "FeatureCollection",
    features: source
      .filter((s) => {
        const cat = s.type || s.category;
        return activeCategories.has(cat);
      })
      .map((s) => {
        const cat = s.type || s.category;
        return {
          type: "Feature",
          properties: {
            id: s.id,
            category: cat,
            name: s.name,
            city: s.city || "",
            phone: s.phone || "",
            color: SERVICE_META[cat]?.color || "#0F1428",
          },
          geometry: { type: "Point", coordinates: [s.lng, s.lat] },
        };
      }),
  };
};
